import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Gavel } from "lucide-react";

const BID_STATUS = {
  pending: { label: "Pending", cls: "text-xd-yellow bg-xd-yellow/10 border-xd-yellow/30" },
  countered: { label: "Countered", cls: "text-xd-blue bg-xd-blue/10 border-xd-blue/30" },
  won: { label: "Won", cls: "text-xd-green bg-xd-green/10 border-xd-green/30" },
  lost: { label: "Not selected", cls: "text-xd-dim bg-white/5 border-xd-border" },
};

function BidBadge({ status }) {
  const s = BID_STATUS[status] || BID_STATUS.pending;
  return <span data-testid={`bid-status-${status}`} className={`inline-flex items-center text-[11px] font-bold uppercase tracking-wide rounded px-2 py-0.5 border ${s.cls}`}>{s.label}</span>;
}

export default function MyBids() {
  const [bids, setBids] = useState(null);
  useEffect(() => { api.get("/carrier/bids").then((r) => setBids(r.data)); }, []);
  if (!bids) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></DashboardShell>;

  return (
    <DashboardShell nav={carrierNav} title="My Bids">
      <p className="text-xd-muted -mt-4 mb-6">Track every bid you've submitted and see where it stands.</p>
      {bids.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-xd-border rounded-xl">
          <Gavel size={32} className="mx-auto text-xd-dim" />
          <p className="mt-3 text-xd-muted">You haven't submitted any bids yet.</p>
          <Button asChild className="mt-4 bg-xd-orange hover:bg-xd-orangeH text-white"><Link to="/find-loads">Find Loads</Link></Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-xd-border">
          <table className="w-full text-sm">
            <thead className="bg-xd-surface text-xd-dim text-xs uppercase tracking-wide">
              <tr>{["XD #", "Lane", "Your Bid", "XD Counter", "Status", "Load", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {bids.map((b) => (
                <tr key={b.id} data-testid={`bid-${b.xd_number}`} className="border-t border-xd-border hover:bg-xd-surface">
                  <td className="px-4 py-3 font-mono text-xs text-xd-dim">{b.xd_number}</td>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{b.origin_city} → {b.dest_city}</td>
                  <td className="px-4 py-3 font-bold">${b.rate?.toLocaleString()}</td>
                  <td className="px-4 py-3">{b.counter_rate ? <span className="font-bold text-xd-blue">${b.counter_rate?.toLocaleString()}</span> : <span className="text-xd-dim">—</span>}</td>
                  <td className="px-4 py-3"><BidBadge status={b.status} /></td>
                  <td className="px-4 py-3 text-xs text-xd-muted">{b.load_status || "—"}</td>
                  <td className="px-4 py-3"><Button asChild size="sm" variant="outline" className="border-xd-border text-white hover:bg-xd-surface"><Link to={`/find-loads/${b.load_id}`}>View</Link></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
